import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FiSend } from "react-icons/fi";
import z from "zod";

import userAtom from "@/atoms/userAtom";
import TextAreaField from "@/components/shared/formFields/textarea";
import Button from "@/components/shared/ui/Button";
import usePostFeedback from "@/hooks/methodologyReview/usePostFeedback";
import { MethodologyReviewComment } from "@/schemas/methodology/reviewComment";
import { MethodologyWithRoundDetails } from "@/types/Methodology";
import { isUserActiveReviewer } from "@/utils/isOriginator";
import { useAtom } from "jotai/index";

interface IProps {
	methodologyDetails: MethodologyWithRoundDetails;
	setIsFeedbackShowing: Dispatch<SetStateAction<boolean>>;
}

const ReviewMethodologyFeedbackComponent = ({ methodologyDetails, setIsFeedbackShowing }: IProps) => {
	const { mutate: submitFeedback, isSuccess, isLoading } = usePostFeedback();
	const [user] = useAtom(userAtom);

	const isActiveReviewer = isUserActiveReviewer({ user, reviewers: methodologyDetails?.reviewers });
	const isUserOriginator = methodologyDetails.originatorId == user?.id;

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<MethodologyReviewComment>({
		resolver: zodResolver(
			z.object({
				comment: z.string().nonempty("Please enter a comment").max(5000, "Comment cannot be more than 5000 characters"),
			}),
		),
		mode: "onBlur",
	});

	const onSubmit = async (data: MethodologyReviewComment) => {
		const dataToSubmit = { comment: data.comment, methodologyRoundId: methodologyDetails.methodologyRoundId };
		submitFeedback(dataToSubmit);
	};

	useEffect(() => {
		if (isSuccess) {
			reset();
		}
	}, [isSuccess, reset, setIsFeedbackShowing]);

	if (!isActiveReviewer && !isUserOriginator) {
		return null;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<h4>Add a Comment</h4>
			<div className="my-4 space-y-3 rounded bg-white p-4">
				<TextAreaField
					{...register("comment")}
					errorMessage={errors.comment?.message}
					label=""
					rows={4}
					placeholder="Write your feedback here"
				/>
				<div className="flex items-start justify-between">
					<p>{`${watch("comment")?.length ?? 0}/5000`}</p>
					<Button type="submit" className="flex items-center space-x-2" disabled={!watch("comment") || isLoading}>
						<span>{isLoading ? "Sending..." : "Send"}</span>
						<span>
							<FiSend />
						</span>
					</Button>
				</div>
			</div>
		</form>
	);
};

export default ReviewMethodologyFeedbackComponent;
